// Metric to imperial conversions, as defined by "Metric_to_imperial_system_conversion.xlsx" (sheets
// Conversione1/2/3 and RIASSUNTO CONVERSIONI). Display only: values are always stored in metric.

const MILLIMETERS_PER_INCH = 25.4;
const FEET_PER_METER = 3.2808399;
const SQUARE_FEET_PER_SQUARE_METER = 10.7639104;
const POUNDS_PER_KILOGRAM = 2.20462262;
const POUNDS_FORCE_PER_KILONEWTON = 224.809;
const PSF_PER_KILONEWTON_PER_SQUARE_METER = 20.885;

// Live loads [kN/m²] -> [psf] for the deck designer's options, as agreed: shown as round values
// (41.8, 62.7, 83.6, 104.4 -> 40, 60, 80, 100). Display only, calculations keep the kN/m² value.
const LIVE_LOAD_PSF: Record<number, number> = { 2: 40, 3: 60, 4: 80, 5: 100 };

// Denominators tried for a fraction of an inch, smallest first: on a tie the smallest one wins. 1/32"
// is only used for a few sizes, see MANUAL_INCH_FRACTION_DENOMINATORS.
const INCH_FRACTION_DENOMINATORS = [1, 2, 4, 8, 16];
// Feet + inches only go down to 1/8" (Conversione3).
const FEET_INCH_FRACTION_DENOMINATORS = [1, 2, 4, 8];

// Sizes the sheet rounds by hand (Conversione2: the "MANUALE" rows and the 1/32" ones): millimeters ->
// denominator.
const MANUAL_INCH_FRACTION_DENOMINATORS: Record<number, number> = {
  1: 32, 5.3: 32, 5.5: 32, 5.6: 32, 7.5: 32, 8.5: 32, 26: 32, 90: 2, 100: 1, 110: 8, 113: 2, 150: 1, 160: 4, 170: 4, 180: 8, 185: 4, 200: 1, 201: 1, 220: 8, 240: 2,
  250: 1, 275: 8, 300: 4, 325: 4, 326: 4, 360: 4, 380: 1, 440: 4, 450: 4, 480: 1, 500: 4, 560: 1, 700: 2,
  900: 2,
};

const roundTo = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

// Excel's ARROTONDA.PER.ECC: rounds up. The epsilon keeps an exact value (e.g. 2.00) from going up.
const roundUpTo = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.ceil(value * factor - 1e-9) / factor;
};

const greatestCommonDivisor = (a: number, b: number): number => (b === 0 ? a : greatestCommonDivisor(b, a % b));

// The denominator whose rounding is closest to the value, relative to it.
const getClosestDenominator = (value: number, denominators: number[]): number => {
  const getError = (denominator: number) => Math.abs(Math.round(value * denominator) / denominator - value) / value;
  return denominators.reduce((best, denominator) => (getError(denominator) < getError(best) ? denominator : best));
};

// e.g. 3.5 -> "3 1/2", 0.25 -> "1/4", 4 -> "4".
const formatFraction = (value: number, denominator: number): string => {
  const total = Math.round(value * denominator);
  const whole = Math.floor(total / denominator);
  const numerator = total % denominator;
  if (numerator === 0) {
    return `${whole}`;
  }

  const divisor = greatestCommonDivisor(numerator, denominator);
  const fraction = `${numerator / divisor}/${denominator / divisor}`;
  return whole === 0 ? fraction : `${whole} ${fraction}`;
};

// Lengths, widths, heights [mm] -> fraction of an inch, e.g. 89 -> 3 1/2" (Conversione2).
export const millimetersToFractionalInches = (millimeters: number): string => {
  const inches = millimeters / MILLIMETERS_PER_INCH;
  if (inches === 0) {
    return `0"`;
  }

  const denominator =
    MANUAL_INCH_FRACTION_DENOMINATORS[millimeters] ?? getClosestDenominator(inches, INCH_FRACTION_DENOMINATORS);
  return `${formatFraction(inches, denominator)}"`;
};

// Lengths, widths, heights [cm] -> fraction of an inch (Conversione1, which reuses Conversione2).
export const centimetersToFractionalInches = (centimeters: number): string =>
  millimetersToFractionalInches(roundTo(centimeters * 10, 6));

// Lengths, widths, heights [m] -> feet + fraction of an inch, e.g. 1.2 -> 3' 11 1/4" (Conversione3).
export const metersToFeetAndInches = (meters: number): string => {
  let feet = Math.floor(meters * FEET_PER_METER);
  const inches = ((meters * FEET_PER_METER - feet) / FEET_PER_METER) * (1000 / MILLIMETERS_PER_INCH);

  let formattedInches =
    inches === 0 ? "0" : formatFraction(inches, getClosestDenominator(inches, FEET_INCH_FRACTION_DENOMINATORS));
  // Rounding up to a whole foot (the sheet would print 12"). Carried over instead.
  if (formattedInches === "12") {
    feet += 1;
    formattedInches = "0";
  }

  if (formattedInches === "0") {
    return `${feet}'`;
  }
  return feet === 0 ? `${formattedInches}"` : `${feet}' ${formattedInches}"`;
};

// [m] -> [ft] with the given decimals (e.g. 1 for heights, 0 for lengths).
export const metersToFeet = (meters: number, digits: number): number => roundTo(meters * FEET_PER_METER, digits);

// Thicknesses, or sizes the product line wants in decimals [mm] -> [in] with the given decimals.
export const millimetersToDecimalInches = (millimeters: number, digits: number): number =>
  roundTo(millimeters / MILLIMETERS_PER_INCH, digits);

// Diameters [mm] -> [in], rounded up to 2 decimals.
export const millimetersToDiameterInches = (millimeters: number): number =>
  roundUpTo(millimeters / MILLIMETERS_PER_INCH, 2);

// Areas [m²] -> [ft²], no decimals.
export const squareMetersToSquareFeet = (squareMeters: number): number =>
  roundTo(squareMeters * SQUARE_FEET_PER_SQUARE_METER, 0);

// Weights [kg] -> [lb], no decimals.
export const kilogramsToPounds = (kilograms: number): number => roundTo(kilograms * POUNDS_PER_KILOGRAM, 0);

// Live loads [kN/m²] -> [psf]: the agreed values for the options, 1 decimal for anything else.
export const kilonewtonsPerSquareMeterToPsf = (kilonewtonsPerSquareMeter: number): number =>
  LIVE_LOAD_PSF[kilonewtonsPerSquareMeter] ??
  roundTo(kilonewtonsPerSquareMeter * PSF_PER_KILONEWTON_PER_SQUARE_METER, 1);

// Loads [kN] -> [lbf], no decimals.
export const kilonewtonsToPoundsForce = (kilonewtons: number): number =>
  roundTo(kilonewtons * POUNDS_FORCE_PER_KILONEWTON, 0);
